import { format } from 'date-fns';
import { useMemoEditor } from '../context/MemoEditorContext';
import { api } from '../lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Memo {
    memo_id: string;
    content: string;
    memo_date: string;
    created_at: string;
    status?: 'ACTIVE' | 'ARCHIVED';
    related_memos?: string[];
    memo_type?: string;
    // Add other fields as needed
}

export function MemoCard({ memo }: { memo: Memo }) {
    const { openEditor } = useMemoEditor();
    const queryClient = useQueryClient();

    const deleteMutation = useMutation({
        mutationFn: api.deleteMemo,
        onMutate: async (memoId) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['memos'] });

            // Snapshot all memo queries for rollback
            const queryCache = queryClient.getQueryCache();
            const memoQueries = queryCache.findAll({ queryKey: ['memos'] });
            const previousQueries = memoQueries.map(query => ({
                queryKey: query.queryKey,
                data: query.state.data
            }));

            // Optimistically remove memo from all caches
            memoQueries.forEach(query => {
                queryClient.setQueryData(query.queryKey, (old: any) => {
                    if (!old?.data) return old;
                    return {
                        ...old,
                        data: old.data.filter((m: any) => m.memo_id !== memoId)
                    };
                });
            });

            return { previousQueries };
        },
        onError: (err, memoId, context) => {
            // Rollback on error
            context?.previousQueries.forEach(({ queryKey, data }) => {
                queryClient.setQueryData(queryKey, data);
            });
            console.error('Failed to delete memo:', err);
            // TODO: Show error toast notification
        },
        onSuccess: () => {
            // Refetch to ensure consistency
            queryClient.invalidateQueries({ queryKey: ['memos'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['memo-dates'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['memo-stats'], refetchType: 'all' });
        },
    });

    const handleEdit = () => {
        openEditor({
            mode: 'edit',
            content: memo.content,
            memoId: memo.memo_id,
            date: new Date(memo.memo_date || memo.created_at),
            status: memo.status || 'ACTIVE',
            relatedMemoId: memo.related_memos?.[0] // Pass first related memo if exists
        });
    };

    const handleFollowUp = () => {
        openEditor({
            mode: 'follow-up',
            content: '',
            date: new Date(),
            relatedMemoId: memo.memo_id
        });
    };

    const handleDelete = () => {
        if (confirm('Are you sure you want to delete this memo?')) {
            deleteMutation.mutate(memo.memo_id);
        }
    };

    const highlightContent = (text: string) => {
        // Enhanced regex to match #, @, or $ followed by:
        // - Word characters (\w includes a-z, A-Z, 0-9, _)
        // - Chinese characters (expanded Unicode range: \u4e00-\u9fff)
        // - Hyphens (-)
        // The pattern continues matching as long as these characters appear
        const parts = text.split(/([#@$][\w\u4e00-\u9fff.-]+)/g);

        return parts.map((part, i) => {
            if (part.startsWith('#')) {
                return <span key={i} className="text-blue-500 font-medium">{part}</span>;
            }
            if (part.startsWith('@')) {
                return <span key={i} className="text-amber-500 font-medium">{part}</span>;
            }
            if (part.startsWith('$')) {
                return <span key={i} className="text-purple-500 font-medium">{part}</span>;
            }
            return <span key={i}>{part}</span>;
        });
    };

    return (
        <div
            className="py-4 border-b border-zinc-100 dark:border-zinc-900 last:border-0 group relative cursor-pointer"
            onDoubleClick={handleEdit}
        >
            <div className="flex items-start justify-between mb-1">
                <span className="text-xs text-zinc-400 font-mono">
                    {format(new Date(memo.memo_date || memo.created_at), 'MMM d, yyyy')}
                    <span className="ml-2 opacity-75">| {format(new Date(memo.memo_date || memo.created_at), 'EEEE')}</span>
                </span>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 p-0 text-zinc-300 hover:text-zinc-600 dark:text-zinc-700 dark:hover:text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                        <DropdownMenuItem onClick={handleEdit}>
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleFollowUp}>
                            Follow-up
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled className="opacity-50">
                            Related
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={handleDelete}
                            className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                        >
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div className="prose dark:prose-invert max-w-none whitespace-pre-wrap text-sm leading-relaxed text-zinc-800 dark:text-zinc-200">
                {highlightContent(memo.content)}
            </div>
        </div>
    );
}
